import React, { useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CalendarGrid } from '../components/CalendarGrid';
import { EventsGridView } from '../components/EventsGridView';
import { EventsListView } from '../components/EventsListView';
import { EventFilters, type EventFilterState } from '../components/EventFilters';
import { Grid, List, Calendar as CalendarIcon, Disc3, Tv, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { events as allEvents } from '../data/events';
const featuredEvents = [{
  icon: Disc3,
  title: 'Live DJ Nights',
  description: 'Every Friday & Saturday featuring top DJs spinning the hottest tracks while you enjoy premium bottle service.',
  detail: 'Fridays & Saturdays • 11PM-3AM'
}, {
  icon: Tv,
  title: 'Sports Viewing Parties',
  description: 'Watch the biggest games on our massive 190ft screen with surround sound and VIP seating options.',
  detail: 'Game Days • Check Schedule'
}, {
  icon: Star,
  title: 'Celebrity Appearances',
  description: 'Meet movie stars, athletes, and entertainers in an exclusive intimate setting with photo opportunities.',
  detail: 'Special Events • By Invitation'
}];
const initialFilters: EventFilterState = {
  search: '',
  type: 'all',
  category: 'all',
  startDate: '',
  endDate: '',
  sort: 'date-asc'
};
export function EventsCalendar() {
  const [view, setView] = useState<'grid' | 'calendar' | 'list'>('grid');
  const [filters, setFilters] = useState<EventFilterState>(initialFilters);
  const handleFilterChange = (next: Partial<EventFilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...next
    }));
  };
  const handleReset = () => setFilters(initialFilters);
  const filteredEvents = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const result = allEvents.filter(event => {
      // Search across title, artist, venue
      if (term) {
        const haystack = `${event.title} ${event.artist} ${event.venue}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      // Event type (day)
      if (filters.type !== 'all' && event.type !== filters.type) return false;
      // Category
      if (filters.category !== 'all' && event.category !== filters.category) return false;
      // Date range
      if (filters.startDate && event.isoDate < filters.startDate) return false;
      if (filters.endDate && event.isoDate > filters.endDate) return false;
      return true;
    });
    const sorted = [...result].sort((a, b) => {
      switch (filters.sort) {
        case 'date-desc':
          return b.isoDate.localeCompare(a.isoDate);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'date-asc':
        default:
          return a.isoDate.localeCompare(b.isoDate);
      }
    });
    return sorted;
  }, [filters]);
  return <div className="min-h-screen bg-[#0B1014] text-white selection:bg-[#DDA247] selection:text-[#0B1014]" data-id="element-1324">
      <Header data-id="element-1325" />

      <main className="pt-32 pb-16 px-4 md:px-8 max-w-[1600px] mx-auto" data-id="element-1326">
        {/* Upcoming Events Hero */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-1327">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-wide" data-id="element-1328">
            UPCOMING EVENTS
          </h1>
          <div className="w-24 h-1 bg-[#DDA247] mx-auto mb-6" data-id="element-1329" />
          <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-1330">
            Experience world-class entertainment on our 190ft immersive video
            wall. From live DJ nights to celebrity appearances, sports viewing
            parties to comedy shows.
          </p>

          <div className="mt-10 inline-flex flex-col items-center" data-id="element-1331">
            <p className="text-[#DDA247] text-xs font-bold uppercase tracking-widest mb-2" data-id="element-1332">
              VIP & Birthday Bookings
            </p>
            <a href="sms:3014549401" className="text-3xl md:text-4xl font-black text-white hover:text-[#DDA247] transition-colors tracking-tighter" data-id="element-1333">
              TEXT 301.454.9401
            </a>
          </div>
        </motion.div>

        {/* Featured Event Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-1334">
          {featuredEvents.map((event, index) => <motion.div key={event.title} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.15,
          duration: 0.6
        }} className="bg-[#11161A] border border-white/5 p-8 text-center" data-id="element-1335">
              <div className="inline-flex items-center justify-center w-14 h-14 mb-5 bg-[#DDA247]/10 rounded-full" data-id="element-1336">
                <event.icon className="w-7 h-7 text-[#DDA247]" data-id="element-1337" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 tracking-wide" data-id="element-1338">
                {event.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-1339">
                {event.description}
              </p>
              <p className="text-[#DDA247] text-xs font-medium tracking-widest uppercase" data-id="element-1340">
                {event.detail}
              </p>
            </motion.div>)}
        </div>

        {/* Filters (apply to grid & list views) */}
        {view !== 'calendar' && <EventFilters filters={filters} onChange={handleFilterChange} onReset={handleReset} resultCount={filteredEvents.length} totalCount={allEvents.length} data-id="element-1341" />}

        {/* View Toggles */}
        <div className="flex justify-end mb-12" data-id="element-1342">
          <div className="flex items-center space-x-2 bg-white/5 p-1 rounded" data-id="element-1343">
            <button onClick={() => setView('grid')} className={`p-3 rounded transition-all duration-300 ${view === 'grid' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-white hover:text-[#DDA247] hover:bg-white/5'}`} aria-label="Grid View" data-id="element-1344">
              <Grid className="w-5 h-5" data-id="element-1345" />
            </button>
            <button onClick={() => setView('calendar')} className={`p-3 rounded transition-all duration-300 ${view === 'calendar' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-white hover:text-[#DDA247] hover:bg-white/5'}`} aria-label="Calendar View" data-id="element-1346">
              <CalendarIcon className="w-5 h-5" data-id="element-1347" />
            </button>
            <button onClick={() => setView('list')} className={`p-3 rounded transition-all duration-300 ${view === 'list' ? 'bg-[#DDA247] text-[#0B1014]' : 'text-white hover:text-[#DDA247] hover:bg-white/5'}`} aria-label="List View" data-id="element-1348">
              <List className="w-5 h-5" data-id="element-1349" />
            </button>
          </div>
        </div>

        {/* Content with Transitions */}
        <AnimatePresence mode="wait" data-id="element-1350">
          {view === 'calendar' && <motion.div key="calendar" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-1351">
              <CalendarGrid data-id="element-1352" />
            </motion.div>}

          {view === 'grid' && <motion.div key="grid" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-1353">
              <EventsGridView events={filteredEvents} data-id="element-1354" />
            </motion.div>}

          {view === 'list' && <motion.div key="list" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} data-id="element-1355">
              <EventsListView events={filteredEvents} data-id="element-1356" />
            </motion.div>}
        </AnimatePresence>
      </main>

      <Footer data-id="element-1357" />
    </div>;
}